/**
 * AP-32 Nachtrag — **Formatierer und Prüfungen der Kopffelder, ohne Datenbank.**
 *
 * Maske und PDF zeigen denselben Text: Liefertermin, Leistungszeitraum,
 * Zahlungstermine, Belegnummer. Stünde die Regel zweimal, liefe sie beim ersten
 * neuen Fall auseinander (Muster: Rechenkern 19.2-29).
 *
 * Daten kommen als `JJJJ-MM-TT` — ohne Uhrzeit und ohne Zeitzone
 * (`zeit-und-datum.md`); gerechnet wird ausschließlich in UTC.
 */

import type { DeliveryDateType, PaymentScheduleEntry } from './documents'
import { DELIVERY_DATE_TYPES, MAX_PAYMENT_SCHEDULE_ENTRIES } from './documents'

export type FeldSprache = 'de' | 'en'

// ─── Belegnummer (6., Nachtrag N-9) ──────────────────────────────────────────

/** Die Platzhalter des Nummernmusters. `{nummer}` ist Pflicht. */
export const NUMMERN_PLATZHALTER = ['{prefix}', '{jahr}', '{jahr2}', '{monat}', '{nummer}'] as const

export const STANDARD_NUMMERNMUSTER = '{prefix}{jahr}-{nummer}'

/** Stellen der Laufnummer: aufgefüllt, darüber einfach länger — eine Anzeigeregel, keine Grenze. */
export const STELLEN_MIN = 1
export const STELLEN_MAX = 9

export interface NummernTeile {
  prefix: string
  /** 0 = durchlaufende Reihe; `{jahr}` bleibt dann leer. */
  jahr: number
  laufnummer: number
  stellen: number
  /** Monat des Belegdatums (1–12) für `{monat}` — nur Anzeige, die Reihe bleibt jährlich bzw. durchlaufend. */
  monat?: number
}

export function formatiereBelegnummer(muster: string, teile: NummernTeile): string {
  const nummer = String(teile.laufnummer).padStart(teile.stellen, '0')
  const jahr = teile.jahr > 0 ? String(teile.jahr) : ''
  const jahr2 = teile.jahr > 0 ? String(teile.jahr).slice(-2) : ''
  const monat = teile.monat ? String(teile.monat).padStart(2, '0') : ''
  return (
    muster
      .split('{prefix}').join(teile.prefix)
      .split('{monat}').join(monat)
      .split('{jahr2}').join(jahr2)
      .split('{jahr}').join(jahr)
      .split('{nummer}').join(nummer)
      // Ohne Jahr bliebe aus `{prefix}{jahr}-{nummer}` ein `RE-0001` — richtig.
      // Ein doppelter Trenner (`RE--0001`) entsteht nur, wenn das Muster selbst
      // einen um das Jahr herum setzt; den glätten wir, statt ihn zu drucken.
      .replace(/([-/._])\1+/g, '$1')
  )
}

export type NummernmusterBefund = 'ok' | 'ohneNummer' | 'unbekannterPlatzhalter' | 'zuLang' | 'ohneJahr'

/**
 * Taugt das Muster? **Ohne `{nummer}` wäre jede Nummer dieselbe.** Und eine
 * Jahresreihe ohne Jahr im Muster erzeugte im nächsten Jahr dieselben Nummern
 * noch einmal (N-8) — der Zähler beginnt dort wieder bei 1.
 */
export function pruefeNummernmuster(muster: string, jaehrlich = false): NummernmusterBefund {
  if (muster.length > 60) return 'zuLang'
  if (!muster.includes('{nummer}')) return 'ohneNummer'
  const platzhalter = muster.match(/\{[^}]*\}/g) ?? []
  if (platzhalter.some((p) => !(NUMMERN_PLATZHALTER as readonly string[]).includes(p))) {
    return 'unbekannterPlatzhalter'
  }
  if (jaehrlich && !muster.includes('{jahr}') && !muster.includes('{jahr2}')) return 'ohneJahr'
  return 'ok'
}

// ─── Kalender ────────────────────────────────────────────────────────────────

const MONATE: Record<FeldSprache, readonly string[]> = {
  de: ['Jänner', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
}

function alsUtc(text: string): Date {
  return new Date(`${text.slice(0, 10)}T00:00:00.000Z`)
}

function alsText(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/** `JJJJ-MM-TT` in der Schreibweise der Belegsprache — ohne den Tag zu verschieben. */
export function datumKurz(text: string | null | undefined, sprache: FeldSprache): string {
  if (!text) return ''
  const [j, m, t] = [text.slice(0, 4), text.slice(5, 7), text.slice(8, 10)]
  return sprache === 'de' ? `${t}.${m}.${j}` : `${t}/${m}/${j}`
}

/** Erster und letzter Tag eines Monats (`JJJJ-MM`). */
export function monatsGrenzen(monat: string): { von: string; bis: string } {
  const [j, m] = monat.split('-').map(Number)
  const von = new Date(Date.UTC(j, m - 1, 1))
  const bis = new Date(Date.UTC(j, m, 0))
  return { von: alsText(von), bis: alsText(bis) }
}

/** Liegt genau ein vollständiger Kalendermonat vor? Dann `JJJJ-MM`, sonst `null`. */
export function ganzerMonat(von: string | null | undefined, bis: string | null | undefined): string | null {
  if (!von || !bis) return null
  const monat = von.slice(0, 7)
  const g = monatsGrenzen(monat)
  return g.von === von.slice(0, 10) && g.bis === bis.slice(0, 10) ? monat : null
}

/** ISO-Kalenderwoche eines Datums: Woche und das Jahr, zu dem sie gehört. */
export function isoWoche(text: string): { woche: number; jahr: number } {
  const d = alsUtc(text)
  const tag = (d.getUTCDay() + 6) % 7 // Montag = 0
  d.setUTCDate(d.getUTCDate() - tag + 3) // Donnerstag derselben Woche entscheidet das Jahr
  const jahr = d.getUTCFullYear()
  const woche = 1 + Math.floor((d.getTime() - Date.UTC(jahr, 0, 1)) / 86400000 / 7)
  return { woche, jahr }
}

/** Der Montag einer ISO-Kalenderwoche als `JJJJ-MM-TT` — so wird eine KW gespeichert (N-20). */
export function montagDerWoche(woche: number, jahr: number): string {
  const vierter = new Date(Date.UTC(jahr, 0, 4))
  const montag = new Date(vierter.getTime())
  montag.setUTCDate(vierter.getUTCDate() - ((vierter.getUTCDay() + 6) % 7) + (woche - 1) * 7)
  return alsText(montag)
}

/** Tage zwischen zwei Daten (`bis − von`). */
export function tageZwischen(von: string, bis: string): number {
  return Math.round((alsUtc(bis).getTime() - alsUtc(von).getTime()) / 86400000)
}

// ─── Leistungszeitraum (N-23) ────────────────────────────────────────────────

export function leistungszeitraumText(
  von: string | null | undefined,
  bis: string | null | undefined,
  sprache: FeldSprache,
): string {
  const monat = ganzerMonat(von, bis)
  if (monat) return `${MONATE[sprache][Number(monat.slice(5, 7)) - 1]} ${monat.slice(0, 4)}`
  const a = datumKurz(von, sprache)
  const b = datumKurz(bis, sprache)
  return a && b && a !== b ? `${a} – ${b}` : a || b
}

// ─── Liefertermin (N-20) ─────────────────────────────────────────────────────

export interface Liefertermin {
  type: DeliveryDateType | string | null | undefined
  date: string | null | undefined
  dateTo: string | null | undefined
  note: string | null | undefined
}

const LIEFER_WORT: Record<FeldSprache, Record<DeliveryDateType, string>> = {
  de: { DATE: 'am', UNTIL: 'bis', APPROX: 'ca.', FIXED: 'Fixtermin', WEEK: 'KW', PERIOD: '', TEXT: '' },
  en: { DATE: 'on', UNTIL: 'by', APPROX: 'approx.', FIXED: 'fixed date', WEEK: 'CW', PERIOD: '', TEXT: '' },
}

export function istLieferterminArt(wert: unknown): wert is DeliveryDateType {
  return typeof wert === 'string' && (DELIVERY_DATE_TYPES as readonly string[]).includes(wert)
}

/** Was fehlt an diesem Liefertermin? `null` = vollständig (oder gar keiner). */
export function pruefeLiefertermin(l: Liefertermin): 'typeUnknown' | 'dateMissing' | 'dateToMissing' | 'rangeInvalid' | 'noteMissing' | null {
  if (!l.type) return null
  if (!istLieferterminArt(l.type)) return 'typeUnknown'
  if (l.type === 'TEXT') return l.note?.trim() ? null : 'noteMissing'
  if (!l.date) return 'dateMissing'
  if (l.type === 'PERIOD') {
    if (!l.dateTo) return 'dateToMissing'
    if (l.dateTo.slice(0, 10) < l.date.slice(0, 10)) return 'rangeInvalid'
  }
  return null
}

export function lieferterminText(l: Liefertermin, sprache: FeldSprache): string | null {
  const note = l.note?.trim() ?? ''
  // Nachtrag 5: Ein Datum ohne Zusatz gilt wie „am".
  if (!l.type && l.date) return lieferterminText({ ...l, type: 'DATE' }, sprache)
  if (!l.type || !istLieferterminArt(l.type)) return note || null
  const w = LIEFER_WORT[sprache][l.type]
  let kern = ''
  if (l.type === 'TEXT') return note || null
  if (l.type === 'WEEK' && l.date) {
    const { woche, jahr } = isoWoche(l.date)
    kern = `${w} ${woche}/${jahr}`
  } else if (l.type === 'PERIOD') {
    kern = [datumKurz(l.date, sprache), datumKurz(l.dateTo, sprache)].filter(Boolean).join(' – ')
  } else if (l.type === 'FIXED') {
    kern = `${datumKurz(l.date, sprache)} (${w})`
  } else {
    kern = `${w} ${datumKurz(l.date, sprache)}`
  }
  return [kern, note].filter(Boolean).join(' · ') || null
}

// ─── Lieferkonditionen (N-19) ────────────────────────────────────────────────

export function lieferkonditionText(incoterm: string | null | undefined, ort: string | null | undefined, text: string | null | undefined): string | null {
  const klausel = incoterm ? `${incoterm}${ort?.trim() ? ` ${ort.trim()}` : ''} (Incoterms® 2020)` : ''
  return [klausel, text?.trim() ?? ''].filter(Boolean).join(' · ') || null
}

// ─── Zahlungstermine (N-22) ──────────────────────────────────────────────────

export type ZahlungsplanBefund = 'ok' | 'tooMany' | 'textMissing' | 'percentInvalid' | 'restNotLast' | 'sumOver' | 'sumNot100'

const HUNDERTSTEL = (p: number) => Math.round(p * 100)

export function pruefeZahlungsplan(eintraege: readonly PaymentScheduleEntry[]): ZahlungsplanBefund {
  if (eintraege.length > MAX_PAYMENT_SCHEDULE_ENTRIES) return 'tooMany'
  let summe = 0
  for (let i = 0; i < eintraege.length; i++) {
    const e = eintraege[i]
    if (!e.text?.trim() || e.text.length > 200) return 'textMissing'
    if (e.percent === null) {
      if (i !== eintraege.length - 1) return 'restNotLast'
      continue
    }
    if (typeof e.percent !== 'number' || !Number.isFinite(e.percent) || e.percent <= 0 || e.percent > 100) return 'percentInvalid'
    // Höchstens zwei Nachkommastellen — 33,333 % wäre ein Betrag, den niemand nachrechnen kann.
    if (Math.abs(HUNDERTSTEL(e.percent) - e.percent * 100) > 1e-6) return 'percentInvalid'
    summe += HUNDERTSTEL(e.percent)
  }
  if (summe > 10000) return 'sumOver'
  const mitRest = eintraege.length > 0 && eintraege[eintraege.length - 1].percent === null
  if (eintraege.length > 0 && !mitRest && summe !== 10000) return 'sumNot100'
  return 'ok'
}

/**
 * Die Beträge der Zahlungstermine in Cent — **gerechnet, nicht gespeichert**.
 * Jede Zeile mit Prozentsatz wird kaufmännisch gerundet; die Restzeile nimmt,
 * was übrig bleibt. Ohne Restzeile nimmt die letzte Zeile die Rundungsdifferenz,
 * damit die Summe den Gesamtbetrag auf den Cent trifft.
 */
export function zahlungsplanBetraege(eintraege: readonly PaymentScheduleEntry[], gesamtCent: number): number[] {
  if (!eintraege.length) return []
  const betraege = eintraege.map((e) => (e.percent === null ? 0 : Math.round((gesamtCent * HUNDERTSTEL(e.percent)) / 10000)))
  const letzte = eintraege.length - 1
  const ohneLetzte = betraege.slice(0, letzte).reduce((a, b) => a + b, 0)
  if (eintraege[letzte].percent === null) {
    betraege[letzte] = gesamtCent - ohneLetzte
  } else if (eintraege.reduce((a, e) => a + HUNDERTSTEL(e.percent ?? 0), 0) === 10000) {
    betraege[letzte] = gesamtCent - ohneLetzte
  }
  return betraege
}

/** Der Vorschlagsknopf „⅓ – ⅓ – Rest" (N-22) — eine Vorlage, keine Regel. */
export function drittelPlan(sprache: FeldSprache): PaymentScheduleEntry[] {
  return sprache === 'de'
    ? [
        { text: 'Anzahlung bei Auftragserteilung', percent: 33.33 },
        { text: 'bei Montagebeginn', percent: 33.33 },
        { text: 'Rest nach Fertigstellung', percent: null },
      ]
    : [
        { text: 'Down payment on order', percent: 33.33 },
        { text: 'At start of installation', percent: 33.33 },
        { text: 'Balance on completion', percent: null },
      ]
}
